import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { sanitizeInput } from '@/lib/validation';
import { supabase } from '@/integrations/supabase/client';
import { Eye, EyeOff, Building2, Lock, UserPlus } from 'lucide-react';

const signInSchema = z.object({
  email: z.string().optional().or(z.literal('')),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

const resetPasswordSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
});

const updatePasswordSchema = z.object({
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const acceptInvitationSchema = z.object({
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type SignInFormData = z.infer<typeof signInSchema>;
type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
type UpdatePasswordFormData = z.infer<typeof updatePasswordSchema>;
type AcceptInvitationFormData = z.infer<typeof acceptInvitationSchema>;

// Rate limiting configuration
const rateLimiter = {
  attempts: new Map<string, { count: number; lastAttempt: number }>(),
  maxAttempts: 5,
  windowMs: 15 * 60 * 1000, // 15 minutes
  
  checkLimit(key: string): boolean {
    const now = Date.now();
    const userAttempts = this.attempts.get(key) || { count: 0, lastAttempt: 0 };
    
    // Reset if window has passed
    if (now - userAttempts.lastAttempt > this.windowMs) {
      userAttempts.count = 0;
    }
    
    if (userAttempts.count >= this.maxAttempts) {
      return false;
    }
    
    userAttempts.count++;
    userAttempts.lastAttempt = now;
    this.attempts.set(key, userAttempts);
    
    return true;
  }
};

const Auth = () => {
  const { user, signIn, signUp } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [invitation, setInvitation] = useState<any>(null);
  const [isInvitationMode, setIsInvitationMode] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [isUpdatePasswordMode, setIsUpdatePasswordMode] = useState(false);
  
  const invitationToken = searchParams.get('invitation');
  const accessToken = searchParams.get('access_token');
  const refreshToken = searchParams.get('refresh_token');

  const signInForm = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const acceptInvitationForm = useForm<AcceptInvitationFormData>({
    resolver: zodResolver(acceptInvitationSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const resetPasswordForm = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
    },
  });

  const updatePasswordForm = useForm<UpdatePasswordFormData>({
    resolver: zodResolver(updatePasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  // Check for password reset or invitation token
  useEffect(() => {
    const checkTokens = async () => {
      // Check for password reset tokens
      if (accessToken && refreshToken) {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        
        if (error) {
          toast.error('Invalid password reset link');
          return;
        }
        
        setIsUpdatePasswordMode(true);
        return;
      }

      // Check for invitation token
      if (invitationToken) {
        try {
          const { data, error } = await supabase
            .from('invitations')
            .select('*')
            .eq('token', invitationToken)
            .eq('status', 'pending')
            .maybeSingle();

          if (error || !data) {
            toast.error('Invalid or expired invitation');
            return;
          }

          // Check if invitation is expired
          if (new Date(data.expires_at) < new Date()) {
            toast.error('This invitation has expired');
            return;
          }

          setInvitation(data);
          setIsInvitationMode(true);
        } catch (error) {
          console.error('Error checking invitation:', error);
          toast.error('Failed to validate invitation');
        }
      }
    };

    checkTokens();
  }, [invitationToken, accessToken, refreshToken]);

  // Redirect if already authenticated
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSignIn = async (data: SignInFormData) => {
    console.log('Form submitted with data:', data);
    console.log('Email value:', data.email);
    console.log('Email length:', data.email?.length);
    
    // Check rate limiting
    if (!rateLimiter.checkLimit(data.email)) {
      toast.error('Too many login attempts. Please try again in 15 minutes.');
      return;
    }

    setIsLoading(true);
    try {
      const sanitizedEmail = sanitizeInput(data.email);
      const { error } = await signIn(sanitizedEmail, data.password);
      
      if (error) {
        toast.error(error.message || 'Failed to sign in');
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptInvitation = async (data: AcceptInvitationFormData) => {
    if (!invitation) return;

    setIsLoading(true);
    try {
      // Create the user account
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: invitation.email,
        password: data.password,
        options: {
          data: {
            first_name: invitation.first_name,
            last_name: invitation.last_name,
          }
        }
      });

      if (signUpError) {
        toast.error(signUpError.message || 'Failed to create account');
        return;
      }

      if (authData.user) {
        // Set the user's role
        await supabase
          .from('user_roles')
          .update({ role: invitation.role })
          .eq('user_id', authData.user.id);

        // Mark invitation as accepted
        await supabase
          .from('invitations')
          .update({ 
            status: 'accepted',
            accepted_at: new Date().toISOString()
          })
          .eq('id', invitation.id);

        toast.success('Account created successfully! Please check your email to verify your account.');
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (data: ResetPasswordFormData) => {
    setIsLoading(true);
    try {
      console.log('Reset password attempt for email:', data.email);
      
      // Use multiple fallback URLs to ensure compatibility
      const currentUrl = window.location.href;
      const origin = window.location.origin;
      const redirectUrl = currentUrl.includes('lovable') 
        ? `${origin}/auth?mode=update-password`
        : `${origin}/auth`;
      
      console.log('Using redirect URL:', redirectUrl);
      
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: redirectUrl
      });
      
      if (error) {
        console.error('Reset password error:', error);
        toast.error(error.message || 'Failed to send reset email');
      } else {
        console.log('Reset password email sent successfully');
        toast.success('Password reset email sent! Check your inbox.');
        setIsResetMode(false);
      }
    } catch (error) {
      console.error('Unexpected error during password reset:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async (data: UpdatePasswordFormData) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: data.password
      });
      
      if (error) {
        toast.error(error.message || 'Failed to update password');
      } else {
        toast.success('Password updated successfully!');
        setIsUpdatePasswordMode(false);
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (isInvitationMode && invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-lg shadow-lg">
          <CardHeader className="space-y-2 text-center pb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="rounded-full bg-success/10 p-3">
                <UserPlus className="h-8 w-8 text-success" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">Welcome to Blue Whale Enterprises</CardTitle>
            <CardDescription className="text-base">
              Complete your account setup by creating a password
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted/50 p-4 rounded-lg space-y-2">
              <p className="text-sm font-medium">Invitation Details:</p>
              <p className="text-sm text-muted-foreground">Email: {invitation.email}</p>
              {invitation.first_name && (
                <p className="text-sm text-muted-foreground">Name: {invitation.first_name} {invitation.last_name}</p>
              )}
              <p className="text-sm text-muted-foreground">Role: {invitation.role ? invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1) : 'Unknown'}</p>
            </div>
            
            <Form {...acceptInvitationForm}>
              <form onSubmit={acceptInvitationForm.handleSubmit(handleAcceptInvitation)} className="space-y-4">
                <FormField
                  control={acceptInvitationForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Create Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="Create a secure password"
                            className="h-12 pr-10"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={acceptInvitationForm.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm your password"
                            className="h-12 pr-10"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button 
                  type="submit" 
                  className="w-full h-12 text-base font-medium" 
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Password update mode
  if (isUpdatePasswordMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-4 pb-8">
            <div className="flex justify-center">
              <div className="rounded-full bg-primary/10 p-3">
                <Lock className="h-8 w-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">Update Password</CardTitle>
            <CardDescription className="text-base">
              Create a new password for your account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Form {...updatePasswordForm}>
              <form onSubmit={updatePasswordForm.handleSubmit(handleUpdatePassword)} className="space-y-4">
                <FormField
                  control={updatePasswordForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter new password"
                            className="h-12 pr-10"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={updatePasswordForm.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm new password"
                            className="h-12 pr-10"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button 
                  type="submit" 
                  className="w-full h-12 text-base font-medium" 
                  disabled={isLoading}
                >
                  {isLoading ? 'Updating Password...' : 'Update Password'}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reset password mode
  if (isResetMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-4 pb-8">
            <div className="flex justify-center">
              <div className="rounded-full bg-primary/10 p-3">
                <Lock className="h-8 w-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">Reset Password</CardTitle>
            <CardDescription className="text-base">
              Enter your email address and we'll send you a password reset link
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Form {...resetPasswordForm}>
              <form onSubmit={(e) => {
                e.preventDefault();
                console.log('Reset form submission triggered');
                console.log('Reset form values:', resetPasswordForm.getValues());
                console.log('Reset form errors:', resetPasswordForm.formState.errors);
                console.log('Reset form is valid:', resetPasswordForm.formState.isValid);
                resetPasswordForm.handleSubmit(handleResetPassword)(e);
              }} className="space-y-4">
                <FormField
                  control={resetPasswordForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Enter your email"
                          className="h-12"
                          {...field}
                          onChange={(e) => {
                            console.log('Reset email input changed:', e.target.value);
                            console.log('Reset field value before:', field.value);
                            field.onChange(e);
                            console.log('Reset field value after:', resetPasswordForm.getValues('email'));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button 
                  type="submit" 
                  className="w-full h-12 text-base font-medium" 
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending Reset Email...' : 'Send Reset Email'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  className="w-full h-12 text-base font-medium" 
                  onClick={() => setIsResetMode(false)}
                >
                  Back to Sign In
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4 pb-8">
          <div className="flex justify-center">
            <img 
              src="/lovable-uploads/eec58224-93bd-48d5-bb38-e8fa7b140620.png" 
              alt="Blue Whale Enterprises Logo" 
              className="h-16 w-16"
            />
          </div>
          <CardTitle className="text-2xl md:text-3xl font-bold">Blue Whale Enterprises</CardTitle>
          <CardDescription className="text-base">
            Sign in to access your inventory management system
          </CardDescription>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              🔒 <strong>Invitation Only:</strong> Access is restricted to authorized users only.
            </p>
          </div>
        </CardHeader>
        <CardContent className="px-6 pb-6">
              <Form {...signInForm}>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  console.log('Form submission triggered');
                  console.log('Current form values:', signInForm.getValues());
                  console.log('Form errors:', signInForm.formState.errors);
                  console.log('Form is valid:', signInForm.formState.isValid);
                  signInForm.handleSubmit(handleSignIn)(e);
                }} className="space-y-6">
              <FormField
                control={signInForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="Enter your email"
                        className="h-12"
                        {...field}
                        onChange={(e) => {
                          console.log('Email input changed:', e.target.value);
                          console.log('Field value before:', field.value);
                          field.onChange(e);
                          console.log('Field value after:', signInForm.getValues('email'));
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={signInForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Enter your password"
                        className="h-12"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button 
                type="submit" 
                className="w-full h-12 text-base font-medium" 
                disabled={isLoading}
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>
          </Form>
          
          <div className="mt-6 text-center">
            <Button 
              type="button" 
              variant="link" 
              className="text-sm text-primary hover:underline p-0 h-auto font-normal"
              onClick={() => setIsResetMode(true)}
            >
              Forgot your password?
            </Button>
          </div>

          <div className="mt-4 text-center">
            <div className="border-t pt-6">
              <p className="text-sm text-muted-foreground mb-2">
                Don't have access?
              </p>
              <p className="text-xs text-muted-foreground">
                Contact your administrator to request an invitation to Blue Whale Enterprises.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;